import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import L, { polygon } from "leaflet";
import { Icon, Message } from 'semantic-ui-react';
import styled, { useTheme } from 'styled-components';

import axios from "axios";
import 'leaflet-path-transform';
import UserDetailsModal from './UserDetailsModal';
import TierCapabilityEditorModal from './TierCapabilityEditorModal';
import { useAuth } from './authContext';
import { isUuid } from '../utils/mapVersioning';

const MenuContainer = styled.div`
  position: absolute;
  right: ${({ $right }) => $right || '12px'};
  bottom: ${({ $bottom, theme }) => $bottom || theme.spacing[6]};
  z-index: ${({ theme }) => theme.zIndex.mapControls};
`;

const MenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 16, 24, 0.35);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => (theme.zIndex?.modal || 2000)};
`;

const MenuModal = styled.div`
  width: min(900px, calc(100vw - 32px));
  max-height: min(720px, calc(100vh - 32px));
  background: linear-gradient(180deg, #f9fbff 0%, #f1f4f9 100%);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  box-shadow: 0 24px 80px rgba(18, 24, 40, 0.25);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 14px;
  padding: 10px 14px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.neutral[900]};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $isMobile }) =>
    $isMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(150px, 1fr))'};
  gap: ${({ $isMobile }) => ($isMobile ? '12px' : '18px')};
  overflow: auto;
  padding-bottom: 6px;
`;

const AppCard = styled.button`
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  border-radius: 16px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.68 : 1)};
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  text-align: center;

  &:hover {
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-2px)')};
    box-shadow: ${({ $disabled }) => ($disabled ? 'none' : '0 18px 30px rgba(15, 23, 42, 0.15)')};
    border-color: ${({ $disabled }) => ($disabled ? 'rgba(15, 23, 42, 0.08)' : 'rgba(15, 23, 42, 0.2)')};
  }
`;

const AppIcon = styled.div`
  width: ${({ $isMobile }) => ($isMobile ? '56px' : '64px')};
  height: ${({ $isMobile }) => ($isMobile ? '56px' : '64px')};
  border-radius: 18px;
  display: grid;
  place-items: center;
  background: ${({ $tint }) => $tint || '#e7edf7'};
  color: #0f172a;
  font-size: ${({ $isMobile }) => ($isMobile ? '24px' : '28px')};
`;

const AppTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral[900]};
  line-height: 1.2;
`;

const AppSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral[600]};
  line-height: 1.3;
  min-height: 32px;
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: auto;
  padding-right: 4px;
`;

const ResultRow = styled.button`
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.68 : 1)};
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? 'rgba(15, 23, 42, 0.08)' : 'rgba(15, 23, 42, 0.2)')};
    box-shadow: ${({ $disabled }) => ($disabled ? 'none' : '0 12px 20px rgba(15, 23, 42, 0.12)')};
  }
`;

const ResultText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ResultTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const ResultMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral[600]};
`;

export default function LoadMenu({ map,
                                select_building,
                                sBuilding,
                                userID,
                                activeUser,
                                activeTourToShow,
                                mapBuildings,
                                apiKey,
                                getApiHeaders,
                                canEditMap,
                                canReadMap,
                                showMapListBar,
                                setShowMapListBar,
                                setShowTourListBar,
                                setShowSideEditBar,
                                setShowTourDetailBar,
                                showTourListBar,
                                showChatBar,
                                setShowChatBar,
                                showShipMenu,
                                setShowShipMenu,
                                openShipMenuWithMode,
                                showAroundMeBar,
                                setShowAroundMeBar,
                                openPortDockEditBar,
                                showFlightTrackingBar,
                                setShowFlightTrackingBar,
                                showFlightPriceBar,
                                openFlightPriceBar,
                                setShowFlightPriceBar,
                                showFlightRouteCreateBar,
                                openFlightRouteCreateBar,
                                setShowFlightRouteCreateBar,
                                showFileShareBar,
                                setShowFileShareBar,
                                isMobile,
                                mapControlsRight,
                                mapControlsBottom,
                                mapControlGap,
                                mapControlSize,
                                onMenuOpen,
                                closeFunctionsMenuSignal,
                                setActiveMapToEdit,
                                fetchFullMapData,
                                userTours,
                                selectTourAndOpenDetail,
                                setShowTrackingDevices: controlledSetShowTrackingDevices }) {
    const [showFunctionsMenu, setShowFunctionsMenu] = useState(false);
    const [showBuildings, setShowBuildings] = useState(false);
    const [pdfMapBoundaries, setPdfMapBoundaries] = useState({});
    const [showUserDetails, setShowUserDetails] = useState(false);
    const [showTierCapabilityEditor, setShowTierCapabilityEditor] = useState(false);
    var buildingMarkers = [];
    const [rectangleLayer, setRectangleLayer] = useState(null);
    const polygonRef = useRef(null);
    const searchInputRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeResultIndex, setActiveResultIndex] = useState(0);
    const [menuNotice, setMenuNotice] = useState(null);
    const setShowTrackingDevices = useCallback((nextValue) => {
      const resolvedValue = typeof nextValue === 'function' ? nextValue(false) : nextValue;
      if (typeof controlledSetShowTrackingDevices === 'function') {
        controlledSetShowTrackingDevices(resolvedValue);
      }
    }, [controlledSetShowTrackingDevices]);


    const theme = useTheme();
    const { userMaps, corporateMaps, handleLogout, authenticatedUser } = useAuth();
    const layersButtonWidth = isMobile ? mapControlSize : '140px';
    const functionsRight = useMemo(() => {
      if (!mapControlsRight || !mapControlGap || !mapControlSize) return null;
      return `calc(${mapControlsRight} + ${mapControlSize} + ${mapControlGap} + ${layersButtonWidth} + ${mapControlGap})`;
    }, [layersButtonWidth, mapControlsRight, mapControlGap, mapControlSize]);

    const toggleFunctionsMenu = useCallback(() => {
      setShowFunctionsMenu((prev) => {
        const next = !prev;
        if (next) {
          onMenuOpen?.();
        }
        return next;
      });
    }, [onMenuOpen]);

    useEffect(() => {
      if (closeFunctionsMenuSignal == null) return;
      setShowFunctionsMenu(false);
    }, [closeFunctionsMenuSignal]);

    useEffect(() => {
      if (!showFunctionsMenu) return;
      setSearchQuery("");
      setMenuNotice(null);
      setActiveResultIndex(0);
      const id = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(id);
    }, [showFunctionsMenu]);

    useEffect(() => {
      const handleKeydown = (event) => {
        const isMenuShortcut =
          event.shiftKey &&
          (event.metaKey || event.ctrlKey) &&
          event.key?.toLowerCase() === "m";

        if (isMenuShortcut) {
          event.preventDefault();
          if (event.repeat) return;
          setShowFunctionsMenu((prev) => {
            const next = !prev;
            if (next) {
              onMenuOpen?.();
            }
            return next;
          });
          return;
        }

        if (event.key === "Escape") {
          setShowFunctionsMenu(false);
        }
      };

      window.addEventListener("keydown", handleKeydown);
      return () => window.removeEventListener("keydown", handleKeydown);
    }, [onMenuOpen]);
 
    
    const toggleTourSideBar = () => {
      if (!showTourListBar) {
        setShowTourListBar();
      } else {
        setShowTourListBar(false);
      }
    }

    const toggleShowMapListBar = () => {
      if (!showMapListBar) {
        setShowMapListBar();
      } else {
        setShowMapListBar(false);
      }
    }

    function buildingStyle(feature) {
      if (feature.properties.important_building == 1){
        return { color: '#0D7805',"weight": 3};
      }
      switch(feature.properties.building){
          case 'castle':
            return { color: '#b31515',"weight": 3};
            break;
          case 'church':
            return { color: '#b31515',"weight": 3};
            break;
          case 'cathedral':
            return { color: '#b31515',"weight": 3};
            break;
          
          default:
            if (feature.properties.name !== null){
              return { color: "#CC7373FF","weight": 1};
            } else {
            return { color: "#0B40B1FF","weight": 1};
            }
      }
    }
  


    // Function to check if user has access to specific features
    const hasAccess = (allowedUserIds) => {
      return allowedUserIds.includes(userID);
    };

    const closeMenu = useCallback(() => {
      setShowFunctionsMenu(false);
    }, []);

    const openUserDetails = useCallback(() => {
      setShowUserDetails(true);
      closeMenu();
    }, [closeMenu]);

    const openTierCapabilityEditor = useCallback(() => {
      setShowTierCapabilityEditor(true);
      closeMenu();
    }, [closeMenu]);

    const openTrackingDevices = useCallback(() => {
      setShowTrackingDevices(true);
      closeMenu();
    }, [closeMenu]);

    const toggleShipMenu = useCallback(() => {
      if (!showShipMenu) {
        setShowShipMenu(true);
      } else {
        setShowShipMenu(false);
      }
      closeMenu();
    }, [closeMenu, setShowShipMenu, showShipMenu]);

    const openShipMenuAtMode = useCallback((routeMode, options = {}) => {
      if (typeof openShipMenuWithMode === "function") {
        openShipMenuWithMode(routeMode, options);
      } else {
        setShowShipMenu(true);
      }
      closeMenu();
    }, [closeMenu, openShipMenuWithMode, setShowShipMenu]);

    const openPortDockEditor = useCallback(() => {
      if (typeof openPortDockEditBar === "function") {
        openPortDockEditBar();
      } else {
        openShipMenuAtMode("inland");
      }
      closeMenu();
    }, [closeMenu, openPortDockEditBar, openShipMenuAtMode]);

    const toggleAroundMe = useCallback(() => {
      if (!showAroundMeBar) {
        setShowAroundMeBar?.(true);
      } else {
        setShowAroundMeBar?.(false);
      }
      closeMenu();
    }, [closeMenu, setShowAroundMeBar, showAroundMeBar]);

    const toggleFlightTracking = useCallback(() => {
      if (!showFlightTrackingBar) {
        setShowFlightTrackingBar();
      } else {
        setShowFlightTrackingBar(false);
      }
      closeMenu();
    }, [closeMenu, setShowFlightTrackingBar, showFlightTrackingBar]);

    const toggleFlightRouteCreate = useCallback(() => {
      if (!showFlightRouteCreateBar) {
        openFlightRouteCreateBar?.(null, "user");
      } else {
        setShowFlightRouteCreateBar(false);
      }
      closeMenu();
    }, [closeMenu, openFlightRouteCreateBar, setShowFlightRouteCreateBar, showFlightRouteCreateBar]);

    const toggleFlightPrices = useCallback(() => {
      if (!showFlightPriceBar) {
        openFlightPriceBar?.(null, "user");
      } else {
        setShowFlightPriceBar(false);
      }
      closeMenu();
    }, [closeMenu, openFlightPriceBar, setShowFlightPriceBar, showFlightPriceBar]);

    const toggleChat = useCallback(() => {
      if (!showChatBar) {
        setShowChatBar();
      } else {
        setShowChatBar(false);
      }
      closeMenu();
    }, [closeMenu, setShowChatBar, showChatBar]);

    const toggleFileShare = useCallback(() => {
      if (!showFileShareBar) {
        setShowFileShareBar();
      } else {
        setShowFileShareBar(false);
      }
      closeMenu();
    }, [closeMenu, setShowFileShareBar, showFileShareBar]);

    const toggleTours = useCallback(() => {
      toggleTourSideBar();
      closeMenu();
    }, [closeMenu, toggleTourSideBar]);

    const openTourDetail = useCallback(() => {
      setShowTourDetailBar(true);
      closeMenu();
    }, [closeMenu, setShowTourDetailBar]);

    const toggleMaps = useCallback(() => {
      toggleShowMapListBar();
      closeMenu();
    }, [closeMenu, toggleShowMapListBar]);

    const runMenuAction = useCallback((entry) => {
      if (!entry) return;
      if (entry.disabled) {
        setMenuNotice(entry.disabledMessage || "Your subscription is required to use this feature.");
        return;
      }
      entry.onSelect?.();
    }, []);

    const isVersionedMapItem = useCallback((mapItem) => {
      if (!mapItem) return false;
      if (mapItem.is_versioned) return true;
      return isUuid(String(mapItem.id || ""));
    }, []);

    const toVersionedMapRootHint = useCallback((mapItem) => {
      if (!mapItem || !isVersionedMapItem(mapItem)) return null;
      return {
        id: mapItem.id,
        name: mapItem.properties?.area_name || mapItem.name || "",
        owner_id: mapItem.properties?.map_user ?? mapItem.owner_id ?? null,
        owner_tags: Array.isArray(mapItem.properties?.owner_tags) ? mapItem.properties.owner_tags : [],
        my_tags: Array.isArray(mapItem.properties?.my_tags) ? mapItem.properties.my_tags : [],
        can_edit: Boolean(mapItem.can_edit),
        current_revision_id: mapItem.current_revision_id || null,
      };
    }, [isVersionedMapItem]);

    const selectMap = useCallback(async (mapItem) => {
      if (!mapItem) return;
      // Open the MapListBar so the MapEditBar will be visible
      setShowMapListBar?.();
      if (mapItem.has_full_details) {
        setActiveMapToEdit?.(mapItem);
      } else if (fetchFullMapData) {
        const fullMapData = await fetchFullMapData(mapItem.id, toVersionedMapRootHint(mapItem));
        if (fullMapData) {
          setActiveMapToEdit?.(fullMapData);
        }
      } else if (mapItem.properties?.mappoints) {
        setActiveMapToEdit?.(mapItem);
      }
      closeMenu();
    }, [closeMenu, fetchFullMapData, setActiveMapToEdit, setShowMapListBar, toVersionedMapRootHint]);

    const menuApps = useMemo(() => {
      const authenticatedUserId = Number(authenticatedUser?.object_id || authenticatedUser?.id);
      const activeUserId = Number(activeUser?.object_id || activeUser?.id);
      const isImpersonating =
        Number.isFinite(authenticatedUserId) &&
        authenticatedUserId > 0 &&
        Number.isFinite(activeUserId) &&
        activeUserId > 0 &&
        authenticatedUserId !== activeUserId;
      const capabilityCodes = Array.isArray(activeUser?.capabilities) && activeUser.capabilities.length > 0
        ? activeUser.capabilities
        : (Array.isArray(activeUser?.subscription?.capabilities) ? activeUser.subscription.capabilities : []);
      const capabilitySet = new Set(capabilityCodes);
      const hasCapabilityList = capabilityCodes.length > 0;
      const hasCapability = (codes, legacyValue = false) => {
        const normalizedCodes = Array.isArray(codes) ? codes : [codes];
        if (capabilitySet.has('*')) {
          return true;
        }
        if (normalizedCodes.some((code) => capabilitySet.has(code))) {
          return true;
        }
        return !hasCapabilityList && Boolean(legacyValue);
      };

      const canUseNavigation = hasCapability('navigation.use', activeUser?.subscription?.can_navigate);
      const canViewRivers = hasCapability(
        'rivers.view',
        activeUser?.subscription?.can_view_rivers ?? activeUser?.subscription?.can_navigate
      );
      const canViewLocks = hasCapability('locks.view', activeUser?.subscription?.can_view_locks);
      const canViewPorts = hasCapability(
        'ports.view',
        activeUser?.subscription?.can_view_ports_docks
      );
      const canEditPorts = hasCapability(
        'ports.edit',
        activeUser?.subscription?.can_edit_ports_docks
      );
      const canViewDocks = hasCapability(
        'docks.view',
        activeUser?.subscription?.can_view_ports_docks ?? activeUser?.subscription?.can_view_locks
      );
      const canEditDocks = hasCapability(
        'docks.edit',
        activeUser?.subscription?.can_edit_ports_docks
      );
      const canViewFlightStatus = hasCapability(
        'flights.status.view',
        activeUser?.subscription?.can_track_flights
      );
      const canUseFlightPrices = hasCapability(
        'flights.price.use',
        activeUser?.subscription?.can_track_flights
      );
      const canManageTours = hasCapability(
        ['tours.create', 'tours.edit'],
        activeUser?.subscription?.can_create_tours ?? activeUser?.subscription?.can_edit_tours
      );
      const canViewMaps = hasCapability(
        ['maps.view', 'maps.create', 'maps.edit'],
        activeUser?.subscription?.can_view_maps ?? activeUser?.subscription?.can_edit_maps
      );
      const canEditMaps = hasCapability(
        ['maps.create', 'maps.edit'],
        activeUser?.subscription?.can_edit_maps
      );
      const navigationUpgradeMessage = "Your subscription is required to use navigation features.";
      const canUseFiles = hasCapability('files.use', true);
      const canManageTierCapabilities = Boolean(authenticatedUser?.is_staff) && !isImpersonating;
      const canManageTrackingDevices = Boolean(activeUser?.is_superuser) && hasCapability('tracking.admin', true);
      const aroundMeObjectLabels = [
        canViewLocks ? 'locks' : null,
        canViewPorts ? 'ports' : null,
        canViewDocks ? 'docks' : null,
      ].filter(Boolean);
      const aroundMeSubtitle = aroundMeObjectLabels.length > 0
        ? `Nearest ${aroundMeObjectLabels.join(', ')}`
        : 'Nearby objects';
      const cruisePlanningItems = [
        {
          id: "sea-navigation",
          label: "Sea navigation",
          keywords: ["ocean"],
          onSelect: () => openShipMenuAtMode("international"),
          disabled: !canUseNavigation,
          disabledMessage: navigationUpgradeMessage,
        },
        {
          id: "river-navigation",
          label: "River navigation",
          onSelect: () => openShipMenuAtMode("inland"),
          disabled: !canUseNavigation,
          disabledMessage: navigationUpgradeMessage,
        },
        {
          id: "river-kilometers",
          label: "River kilometers",
          onSelect: () => openShipMenuAtMode("inland", { activateRiverMarker: true }),
          isVisible: canViewRivers || canUseNavigation,
        },
        {
          id: "around-me",
          label: "Around Me",
          keywords: ["nearest", ...aroundMeObjectLabels],
          onSelect: toggleAroundMe,
          isVisible: canViewLocks || canViewPorts || canViewDocks,
        },
      ].filter((item) => item.isVisible !== false);

      return [
        {
          id: "user-details",
          title: "User Details",
          subtitle: "Profile & plan info",
          icon: "user",
          tint: "#eaf2ff",
          onSelect: openUserDetails,
          items: [
            { id: "profile", label: "Profile details" },
            { id: "subscription", label: "Subscription status" },
          ],
        },
        {
          id: "tier-capability-editor",
          title: "Tier Capability Editor",
          subtitle: "Allow or deny capabilities per tier",
          icon: "sliders horizontal",
          tint: "#eaf5ff",
          isVisible: canManageTierCapabilities,
          onSelect: openTierCapabilityEditor,
          items: [
            {
              id: "tier-capability-editor-main",
              label: "Edit subscription tier capabilities",
              keywords: ["staff", "subscription", "tier", "allow", "deny", "capability"],
              onSelect: openTierCapabilityEditor,
            },
          ],
        },
        {
          id: "tracking-devices",
          title: "Tracking Devices",
          subtitle: "OwnTracks devices & alarms",
          keywords: ["owntracks", "tracking", "devices", "device history", "tracking alarms", "ship tracking"],
          icon: "map marker alternate",
          tint: "#e0f2fe",
          isVisible: canManageTrackingDevices,
          onSelect: openTrackingDevices,
          items: [
            {
              id: "tracking-devices-panel",
              label: "Manage tracking devices",
              keywords: ["owntracks", "tracking", "devices", "alarms", "history"],
              onSelect: openTrackingDevices,
            },
          ],
        },
        {
          id: "ship-menu",
          title: "Cruise Planning",
          subtitle: canUseNavigation ? "Navigation & ports" : "Navigation requires subscription",
          icon: "ship",
          tint: "#e7f7f2",
          isVisible: true,
          onSelect: toggleShipMenu,
          items: cruisePlanningItems,
        },
        {
          id: "around-me",
          title: "Around Me",
          subtitle: aroundMeSubtitle,
          icon: "crosshairs",
          tint: "#fff4e6",
          isVisible: canViewLocks || canViewPorts || canViewDocks,
          onSelect: toggleAroundMe,
          items: [
            { id: "around-me-main", label: "Find nearest objects", onSelect: toggleAroundMe },
          ],
        },
        {
          id: "port-dock-editor",
          title: "Port & Dock Editor",
          subtitle: canEditPorts
            ? (canEditDocks ? "Create/edit ports and docks" : "Create/edit ports")
            : "Ports editing unavailable",
          icon: "edit",
          tint: "#e6f3ff",
          isVisible: canEditPorts,
          onSelect: openPortDockEditor,
          items: [
            {
              id: "port-dock-editor-item",
              label: canEditDocks ? "Create/Edit Ports and Docking Locations" : "Create/Edit Ports",
              keywords: ["port", "docking", "dock", "location"],
              onSelect: openPortDockEditor,
            },
          ],
        },
        {
          id: "flight-tracking-suite",
          title: "Live Flight Status",
          subtitle: "Delays, gates, real-time updates",
          icon: "plane",
          tint: "#fff0e6",
          isVisible: canViewFlightStatus,
          onSelect: toggleFlightTracking,
          items: [
            { id: "flight-tracking", label: "Track flight", onSelect: toggleFlightTracking },
          ],
        },
        {
          id: "flight-prices-suite",
          title: "Flight Price Planner",
          subtitle: "Compare fares and route costs",
          icon: "line graph",
          tint: "#fff0e6",
          isVisible: canUseFlightPrices,
          onSelect: toggleFlightPrices,
          items: [
            { id: "flight-route", label: "Plan route cost", onSelect: toggleFlightRouteCreate },
            { id: "flight-prices", label: "Check fares", onSelect: toggleFlightPrices },
          ],
        },
        {
          id: "files",
          title: "File Sharing",
          subtitle: "Share & manage",
          icon: "share alternate",
          tint: "#e9f6ff",
          isVisible: canUseFiles,
          onSelect: toggleFileShare,
          items: [
            { id: "send-files", label: "Send files" },
            { id: "receive-files", label: "Receive files" },
          ],
        },
        {
          id: "tours",
          title: "Manage Tours",
          subtitle: "Create & organize",
          icon: "book",
          tint: "#fef3c7",
          isVisible: canManageTours,
          onSelect: toggleTours,
          items: [
            { id: "tour-builder", label: "Tour builder" },
            { id: "tour-collections", label: "Tour collections" },
          ],
        },
        {
          id: "tour-detail",
          title: "Tour Detail",
          subtitle: "Active tour info",
          icon: "info circle",
          tint: "#e8f0ff",
          isVisible: Boolean(activeTourToShow),
          onSelect: openTourDetail,
          items: [
            { id: "tour-overview", label: "Overview" },
            { id: "tour-stops", label: "Stops & media" },
          ],
        },
        {
          id: "maps",
          title: "Manage Maps",
          subtitle: "Layers & assets",
          icon: "map",
          tint: "#e7f6ec",
          isVisible: canViewMaps || canEditMaps,
          onSelect: toggleMaps,
          items: [
            { id: "map-library", label: "Map library" },
            { id: "map-settings", label: "Map settings" },
          ],
        },
      ];
    }, [
      activeTourToShow,
      activeUser?.capabilities,
      activeUser?.id,
      activeUser?.object_id,
      activeUser?.is_superuser,
      activeUser?.subscription?.can_create_tours,
      activeUser?.subscription?.can_edit_maps,
      activeUser?.subscription?.can_edit_tours,
      activeUser?.subscription?.can_navigate,
      activeUser?.subscription?.can_view_maps,
      activeUser?.subscription?.can_view_rivers,
      activeUser?.subscription?.can_view_locks,
      activeUser?.subscription?.can_view_ports_docks,
      activeUser?.subscription?.can_edit_ports_docks,
      activeUser?.subscription?.can_track_flights,
      activeUser?.subscription?.capabilities,
      authenticatedUser?.id,
      authenticatedUser?.is_staff,
      authenticatedUser?.object_id,
      openTierCapabilityEditor,
      openTrackingDevices,
      openTourDetail,
      openUserDetails,
      openPortDockEditor,
      toggleFileShare,
      toggleFlightPrices,
      toggleFlightRouteCreate,
      toggleFlightTracking,
      toggleMaps,
      openShipMenuAtMode,
      toggleShipMenu,
      toggleAroundMe,
      toggleTours,
    ]);

    const visibleApps = useMemo(() => {
      const authenticatedUserId = Number(authenticatedUser?.object_id || authenticatedUser?.id);
      const activeUserId = Number(activeUser?.object_id || activeUser?.id);
      const isImpersonating =
        Number.isFinite(authenticatedUserId) &&
        authenticatedUserId > 0 &&
        Number.isFinite(activeUserId) &&
        activeUserId > 0 &&
        authenticatedUserId !== activeUserId;
      const canManageTierCapabilities = Boolean(authenticatedUser?.is_staff) && !isImpersonating;
      const hasMenuAccess = Boolean(
        menuApps.some((app) => app.id !== "user-details" && (app.isVisible === undefined ? true : app.isVisible)) ||
        canManageTierCapabilities
      );
      const baseApps = hasMenuAccess
        ? menuApps
        : menuApps.filter((app) => app.id === 'user-details');
      return baseApps.filter((app) => (app.isVisible === undefined ? true : app.isVisible));
    }, [activeUser?.id, activeUser?.object_id, authenticatedUser?.id, authenticatedUser?.is_staff, authenticatedUser?.object_id, menuApps]);

    const normalizedQuery = searchQuery.trim().toLowerCase();

    const selectTour = useCallback((tour) => {
      if (!tour) return;
      if (typeof selectTourAndOpenDetail === 'function') {
        selectTourAndOpenDetail(tour);
      } else {
        setShowTourDetailBar?.(true);
      }
      closeMenu();
    }, [closeMenu, selectTourAndOpenDetail, setShowTourDetailBar]);

    const formatTourStartDate = useCallback((tour) => {
      const startDate = tour?.start_date ? new Date(tour.start_date) : null;
      if (!startDate || Number.isNaN(startDate.getTime())) return null;
      return startDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    }, []);

    const getTourDateRange = useCallback((tour) => {
      const startDate = tour?.start_date ? new Date(tour.start_date) : null;
      if (!startDate || Number.isNaN(startDate.getTime())) {
        return { startDate: null, endDate: null };
      }

      const endDate = new Date(startDate);
      const durationDays = Number(tour?.duration || 1);
      endDate.setDate(endDate.getDate() + Math.max(durationDays, 1) - 1);
      endDate.setHours(23, 59, 59, 999);

      return { startDate, endDate };
    }, []);

    const searchResults = useMemo(() => {
      if (!normalizedQuery) return [];
      const results = [];
      const matches = (text) => text?.toLowerCase().includes(normalizedQuery);

      visibleApps.forEach((app) => {
        const appText = [app.title, app.subtitle, ...(app.keywords || [])].filter(Boolean);
        const appMatches = appText.some(matches);
        const items = app.items || [];

        if (items.length === 0) {
          if (appMatches) {
            results.push({ type: 'function', app, item: null, action: app });
          }
          return;
        }

        if (appMatches) {
          items.forEach((item) => {
            results.push({ type: 'function', app, item, action: item.onSelect ? item : app });
          });
          return;
        }

        items.forEach((item) => {
          const itemText = [item.label, ...(item.keywords || [])].filter(Boolean);
          if (itemText.some(matches)) {
            results.push({ type: 'function', app, item, action: item.onSelect ? item : app });
          }
        });
      });

      const onlyUserDetails = visibleApps.length === 1 && visibleApps[0]?.id === 'user-details';
      if (!onlyUserDetails) {
        const upcomingTourResults = [];
        const historicTourResults = [];
        const mapResults = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const allTours = Array.isArray(userTours) ? userTours : [];
        allTours.forEach((tour) => {
          const tourName = tour?.name || tour?.title || `Tour ${tour?.id || ''}`.trim();
          const tourCode = tour?.code || '';
          const portNames = (tour?.port_orders || tour?.ports_of_call || [])
            .map((port) => (
              port?.port_of_call?.name ||
              port?.port?.name ||
              port?.name ||
              port?.city ||
              ''
            ))
            .filter(Boolean);
          const tourText = [
            tourName,
            tourCode,
            tour?.description,
            tour?.tour_type?.name,
            ...portNames,
          ].filter(Boolean);
          if (tourText.some(matches)) {
            const startDateLabel = formatTourStartDate(tour);
            const portPreview = portNames.length > 0 ? portNames.slice(0, 3).join(', ') : '';
            const subtitleParts = [
              startDateLabel ? `Start: ${startDateLabel}` : 'Start date TBD',
              portPreview,
            ].filter(Boolean);
            const { startDate, endDate } = getTourDateRange(tour);
            const tourResult = {
              type: 'tour',
              tour,
              id: tour.id,
              title: tourName,
              subtitle: subtitleParts.join(' - '),
              startTime: startDate?.getTime?.() ?? Number.POSITIVE_INFINITY,
              endTime: endDate?.getTime?.() ?? Number.POSITIVE_INFINITY,
            };

            if (endDate && endDate < today) {
              historicTourResults.push(tourResult);
            } else {
              upcomingTourResults.push(tourResult);
            }
          }
        });

        const seenMapIds = new Set();
        const allMaps = [...(userMaps || []), ...(corporateMaps || [])].filter((mapItem) => {
          const mapId = String(mapItem?.id ?? mapItem?.properties?.id ?? '');
          if (!mapId) return true;
          if (seenMapIds.has(mapId)) return false;
          seenMapIds.add(mapId);
          return true;
        });
        allMaps.forEach((mapItem) => {
          const areaName = mapItem?.properties?.area_name || mapItem?.area_name || mapItem?.name || '';
          const countryName = mapItem?.properties?.country_name || mapItem?.country_name || '';
          const mapCode = mapItem?.properties?.api_key || mapItem?.api_key || '';
          const mapText = [areaName, countryName, mapCode].filter(Boolean);
          if (mapText.some(matches)) {
            mapResults.push({
              type: 'map',
              map: mapItem,
              id: mapItem.id ?? mapItem?.properties?.id,
              title: areaName || `Map ${mapItem.id ?? mapItem?.properties?.id ?? ''}`.trim(),
              subtitle: countryName || 'Map',
            });
          }
        });

        upcomingTourResults.sort((a, b) => a.startTime - b.startTime);
        historicTourResults.sort((a, b) => b.startTime - a.startTime);
        results.push(...upcomingTourResults, ...mapResults, ...historicTourResults);
      }

      return results;
    }, [formatTourStartDate, getTourDateRange, normalizedQuery, visibleApps, userMaps, corporateMaps, userTours]);

    useEffect(() => {
      setActiveResultIndex(0);
    }, [normalizedQuery]);

    const handleSearchKeyDown = (event) => {
      if (!normalizedQuery || searchResults.length === 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveResultIndex((prev) => (prev + 1) % searchResults.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveResultIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
      } else if (event.key === "Enter") {
        event.preventDefault();
        const selected = searchResults[activeResultIndex];
        if (selected) {
          if (selected.type === 'map') {
            selectMap(selected.map);
          } else if (selected.type === 'tour') {
            selectTour(selected.tour);
          } else {
            runMenuAction(selected.action || selected.item || selected.app);
          }
        }
      }
    };

    return (
        <MenuContainer $right={functionsRight} $bottom={mapControlsBottom}>
        <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={toggleFunctionsMenu}
              style={{
                width: isMobile ? theme.components.button.size.md : '140px',
                height: theme.components.button.size.md,
                borderRadius: isMobile ? theme.components.button.borderRadius : '22px',
                border: '1px solid rgba(0,0,0,0.15)',
                background: 'rgba(255,255,255,0.95)',
                boxShadow: theme.shadows.md,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? 0 : '10px',
                cursor: 'pointer',
                padding: isMobile ? 0 : '0 16px',
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.base,
                color: theme.colors.neutral[800],
              }}
            >
              <Icon name='th' />
              {!isMobile && <span>Menu</span>}
            </button>
            {showFunctionsMenu && (
              <MenuOverlay
                data-scroll-overlay="true"
                onClick={() => setShowFunctionsMenu(false)}
                role="presentation"
              >
                <MenuModal data-scroll-overlay="true" onClick={(event) => event.stopPropagation()}>
                  <SearchRow>
                    <Icon name="search" />
                    <SearchInput
                      ref={searchInputRef}
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="Search apps, tools, or tasks"
                      aria-label="Search menu"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          color: theme.colors.neutral[500],
                        }}
                      >
                        <Icon name="close" />
                      </button>
                    )}
                  </SearchRow>

                  {menuNotice && (
                    <Message
                      info
                      size="small"
                      onDismiss={() => setMenuNotice(null)}
                      content={menuNotice}
                      style={{ margin: 0 }}
                    />
                  )}

                  {normalizedQuery ? (
                    <ResultsList>
                      {searchResults.length === 0 && (
                        <div
                          style={{
                            padding: theme.spacing[4],
                            textAlign: 'center',
                            color: theme.colors.neutral[500],
                          }}
                        >
                          No matches. Try another keyword.
                        </div>
                      )}
                      {searchResults.map((result, index) => {
                        const isMapResult = result.type === 'map';
                        const isTourResult = result.type === 'tour';
                        const selectedEntry = result.action || result.item || result.app;
                        const isDisabled = !isMapResult && !isTourResult && Boolean(selectedEntry?.disabled);
                        return (
                          <ResultRow
                            key={isMapResult ? `map-${result.id}` : isTourResult ? `tour-${result.id}` : `${result.app.id}-${result.item?.id || 'app'}`}
                            type="button"
                            onClick={isMapResult ? () => selectMap(result.map) : isTourResult ? () => selectTour(result.tour) : () => runMenuAction(selectedEntry)}
                            $disabled={isDisabled}
                            aria-disabled={isDisabled}
                            style={{
                              borderColor:
                                index === activeResultIndex ? 'rgba(15, 23, 42, 0.35)' : undefined,
                              boxShadow:
                                index === activeResultIndex
                                  ? '0 12px 20px rgba(15, 23, 42, 0.15)'
                                  : undefined,
                            }}
                          >
                            <AppIcon $tint={isMapResult ? '#e7f6ec' : isTourResult ? '#fef3c7' : result.app.tint} $isMobile={isMobile}>
                              <Icon name={isMapResult ? 'map' : isTourResult ? 'book' : result.app.icon} />
                            </AppIcon>
                            <ResultText>
                              <ResultTitle>{isMapResult || isTourResult ? result.title : (result.item?.label || result.app.title)}</ResultTitle>
                              <ResultMeta>
                                {isMapResult || isTourResult
                                  ? result.subtitle
                                  : (isDisabled ? (selectedEntry?.disabledMessage || result.app.title) : result.app.title)}
                              </ResultMeta>
                            </ResultText>
                          </ResultRow>
                        );
                      })}
                    </ResultsList>
                  ) : (
                    <AppsGrid $isMobile={isMobile}>
                      {visibleApps.map((app) => (
                        <AppCard
                          key={app.id}
                          type="button"
                          onClick={() => runMenuAction(app)}
                          $disabled={Boolean(app.disabled)}
                          aria-disabled={Boolean(app.disabled)}
                        >
                          <AppIcon $tint={app.tint} $isMobile={isMobile}>
                            <Icon name={app.icon} />
                          </AppIcon>
                          <AppTitle>{app.title}</AppTitle>
                          <AppSubtitle>{app.subtitle}</AppSubtitle>
                        </AppCard>
                      ))}
                    </AppsGrid>
                  )}
                </MenuModal>
              </MenuOverlay>
            )}
          </div>

        {/* User Details Modal */}
        <UserDetailsModal
          open={showUserDetails}
          onClose={() => setShowUserDetails(false)}
          getApiHeaders={getApiHeaders}
          activeUser={activeUser}
          onLogout={handleLogout}
        />

        <TierCapabilityEditorModal
          open={showTierCapabilityEditor}
          onClose={() => setShowTierCapabilityEditor(false)}
          getApiHeaders={getApiHeaders}
        />

        </MenuContainer>
      )
    }
